// Localized rain showers — periodically a 2–4 hex cell grows cloud puffs,
// rains streaks, and dissolves. Terrain-weighted spawning (weather.ts):
// mountains/forests draw rain, desert/ocean never.
//
// Rendering: rain = one LineSegments (streak pairs falling and wrapping),
// clouds = one Points puff cluster; both share the shower's phase alpha, so
// the whole cell fades in → holds → fades out with a single material each.
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import {
  type RainCandidate,
  buildRainCandidates,
  pickRainCandidate,
  showerAlpha,
  showerDuration,
} from './weather.js';

interface RainShowerProps {
  readonly tiles: ReadonlyArray<Parameters<typeof buildRainCandidates>[0][number]>;
  /** Board scale — showers live inside the scaled group (pass 1 normally). */
  readonly detail?: number;
}

const FADE_IN = 4;
const HOLD = 22;
const FADE_OUT = 6;
const DURATION = showerDuration(FADE_IN, HOLD, FADE_OUT);
/** Quiet stretch between showers (also delays the first one). */
const COOLDOWN_MIN = 25;
const COOLDOWN_RANGE = 55;
const RAIN_DROPS = 130;
const CLOUD_PUFFS = 10;
const FALL_HEIGHT = 3.2;
const RAIN_DROP_SPEED = 5.5; // world units/s

function softTexture(inner: string, outer: string): THREE.CanvasTexture {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, inner);
  g.addColorStop(1, outer);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return new THREE.CanvasTexture(canvas);
}

interface ActiveShower {
  readonly x: number;
  readonly z: number;
  readonly radius: number;
  readonly baseY: number;
  readonly startAt: number;
}

export function RainShower({ tiles }: RainShowerProps) {
  const rainRef = useRef<THREE.LineSegments>(null);
  const cloudRef = useRef<THREE.Points>(null);
  const showerRef = useRef<ActiveShower | null>(null);

  const candidates = useMemo(() => buildRainCandidates(tiles), [tiles]);

  // Rain streak pairs: [x, yTop, z, x, yBottom, z] per drop, filled per frame.
  const rainGeo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(RAIN_DROPS * 6), 3));
    return g;
  }, []);
  const rainMat = useMemo(
    () =>
      new THREE.LineBasicMaterial({
        color: 0x9db8d8,
        transparent: true,
        opacity: 0,
        depthWrite: false,
      }),
    [],
  );

  const cloudGeo = useMemo(() => {
    const g = new THREE.BufferGeometry();
    const pos = new Float32Array(CLOUD_PUFFS * 3);
    for (let i = 0; i < CLOUD_PUFFS; i++) {
      // Puff offsets filled once — slow drift in useFrame adds the motion.
      pos[i * 3] = (Math.random() * 2 - 1) * 1.6;
      pos[i * 3 + 1] = FALL_HEIGHT + 1.6 + Math.random() * 0.9;
      pos[i * 3 + 2] = (Math.random() * 2 - 1) * 1.6;
    }
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    return g;
  }, []);
  const cloudMat = useMemo(
    () =>
      new THREE.PointsMaterial({
        map: softTexture('rgba(225,232,240,0.95)', 'rgba(190,200,214,0)'),
        color: 0xdfe6ee,
        size: 2.6,
        sizeAttenuation: true,
        transparent: true,
        opacity: 0,
        depthWrite: false,
      }),
    [],
  );

  // Spawn loop: quiet cooldown, then a shower over a weighted cell.
  useEffect(() => {
    if (candidates.length === 0) return;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = (delay: number) => {
      timer = setTimeout(() => {
        const pick = pickRainCandidate(candidates);
        if (!pick) return;
        const radius = 2 + Math.floor(Math.random() * 3); // 2–4 hex
        showerRef.current = {
          x: pick.x,
          z: pick.z,
          radius,
          baseY: pick.baseY,
          startAt: performance.now() / 1000,
        };
        schedule((DURATION + COOLDOWN_MIN + Math.random() * COOLDOWN_RANGE) * 1000);
      }, delay);
    };
    schedule((COOLDOWN_MIN + Math.random() * COOLDOWN_RANGE) * 1000);
    return () => clearTimeout(timer);
  }, [candidates]);

  // Per-drop random columns (offset within the disc + phase), stable across frames.
  const dropColumns = useMemo(
    () =>
      Array.from({ length: RAIN_DROPS }, () => {
        const a = Math.random() * Math.PI * 2;
        const r = Math.sqrt(Math.random());
        return { a, r, phase: Math.random(), ox: (Math.random() * 2 - 1) * 0.15 };
      }),
    [],
  );

  useEffect(
    () => () => {
      rainGeo.dispose();
      rainMat.dispose();
      cloudGeo.dispose();
      cloudMat.dispose();
    },
    [rainGeo, rainMat, cloudGeo, cloudMat],
  );

  useFrame(({ clock }) => {
    const shower = showerRef.current;
    const rain = rainRef.current;
    const cloud = cloudRef.current;
    if (!rain || !cloud) return;

    const alpha = shower ? showerAlpha(clock.elapsedTime - shower.startAt) : 0;
    rain.visible = cloud.visible = alpha > 0.01;
    rainMat.opacity = alpha * 0.5;
    cloudMat.opacity = alpha * 0.75;
    if (!shower || alpha <= 0.01) {
      rain.visible = cloud.visible = false;
      return;
    }

    const t = clock.elapsedTime;
    const attr = rain.geometry.getAttribute('position') as THREE.BufferAttribute;
    const arr = attr.array as Float32Array;
    for (let i = 0; i < RAIN_DROPS; i++) {
      const d = dropColumns[i]!;
      const x = shower.x + Math.cos(d.a) * d.r * shower.radius + d.ox;
      const z = shower.z + Math.sin(d.a) * d.r * shower.radius + d.ox;
      const yTop =
        shower.baseY +
        1.2 +
        FALL_HEIGHT -
        ((t * RAIN_DROP_SPEED + d.phase * FALL_HEIGHT) % FALL_HEIGHT);
      arr[i * 6] = x;
      arr[i * 6 + 1] = yTop;
      arr[i * 6 + 2] = z;
      arr[i * 6 + 3] = x;
      arr[i * 6 + 4] = yTop - 0.28; // streak length
      arr[i * 6 + 5] = z;
    }
    attr.needsUpdate = true;

    // Clouds drift in a slow circle above the cell.
    const cpos = cloud.geometry.getAttribute('position') as THREE.BufferAttribute;
    for (let i = 0; i < CLOUD_PUFFS; i++) {
      cpos.setX(i, shower.x + Math.sin(t * 0.05 + i * 1.7) * 1.4);
      cpos.setZ(i, shower.z + Math.cos(t * 0.04 + i * 2.3) * 1.4);
      cpos.setY(i, shower.baseY + FALL_HEIGHT + 1.5 + (i % 3) * 0.4);
    }
    cpos.needsUpdate = true;
  });

  return (
    <>
      <lineSegments
        ref={rainRef}
        geometry={rainGeo}
        material={rainMat}
        frustumCulled={false}
        visible={false}
      />
      <points
        ref={cloudRef}
        geometry={cloudGeo}
        material={cloudMat}
        frustumCulled={false}
        visible={false}
      />
    </>
  );
}
